/**
 * Pricing Tier Framework
 * 
 * Defines subscription tiers with exact USD pricing and feature sets.
 * Modular, isolated - inactive by default.
 */

import type { SubscriptionPlan, SubscriptionTier } from "./types";

/**
 * Subscription Plans Configuration
 * 
 * Exact pricing as specified in directive:
 * - Free: $0
 * - Personal: $24/mo
 * - Pro: $79/mo
 * - Business: $249/mo
 * - Enterprise: Custom
 */
export const SUBSCRIPTION_PLANS: Record<SubscriptionTier, SubscriptionPlan> = {
  free: {
    id: "free",
    name: "Free",
    priceUSD: 0,
    aiCount: 1, // Limited AI
    memoryDepth: "short",
    executionLimits: {
      messagesPerDay: 100,
      apiCallsPerDay: 50,
      fileUploadsPerDay: 10,
      mediaGenerationsPerMonth: 0,
    },
    agentAutonomy: 0, // No agent execution
    features: {
      chat: true,
      agentExecution: false,
      externalTools: false,
      persistentMemory: false,
      projects: false,
      knowledge: false,
      approvalPrompts: false,
      multiAgentWorkflows: false,
      auditLogs: false,
      adminControls: false,
      spendingLimits: false,
      sharedAIs: false,
      multipleUsers: false,
    },
  },
  personal: {
    id: "personal",
    name: "Personal",
    priceUSD: 24,
    aiCount: 1, // 1 full custom AI
    memoryDepth: "medium",
    executionLimits: {
      messagesPerDay: 1000,
      apiCallsPerDay: 500,
      fileUploadsPerDay: 100,
      mediaGenerationsPerMonth: 50,
    },
    agentAutonomy: 1, // Agent Tier 0-1
    features: {
      chat: true,
      agentExecution: true,
      externalTools: false, // No external tools
      persistentMemory: true,
      projects: true,
      knowledge: true,
      approvalPrompts: true,
      multiAgentWorkflows: false,
      auditLogs: false,
      adminControls: false,
      spendingLimits: false,
      sharedAIs: false,
      multipleUsers: false,
    },
  },
  pro: {
    id: "pro",
    name: "Pro",
    priceUSD: 79,
    aiCount: 5, // 3-5 AIs
    memoryDepth: "long",
    executionLimits: {
      messagesPerDay: 10000,
      apiCallsPerDay: 5000,
      fileUploadsPerDay: 1000,
      mediaGenerationsPerMonth: 200,
    },
    agentAutonomy: 2, // Agent Tier 2
    features: {
      chat: true,
      agentExecution: true,
      externalTools: true,
      persistentMemory: true,
      projects: true,
      knowledge: true,
      approvalPrompts: true,
      multiAgentWorkflows: true,
      auditLogs: true,
      adminControls: false,
      spendingLimits: false,
      sharedAIs: false,
      multipleUsers: false,
    },
  },
  business: {
    id: "business",
    name: "Business",
    priceUSD: 249,
    aiCount: -1, // Unlimited
    memoryDepth: "unlimited",
    executionLimits: {
      messagesPerDay: -1, // Unlimited
      apiCallsPerDay: -1, // Unlimited
      fileUploadsPerDay: -1, // Unlimited
      mediaGenerationsPerMonth: -1, // Unlimited
    },
    agentAutonomy: 3, // Agent Tier 3 (with approvals)
    features: {
      chat: true,
      agentExecution: true,
      externalTools: true,
      persistentMemory: true,
      projects: true,
      knowledge: true,
      approvalPrompts: true,
      multiAgentWorkflows: true,
      auditLogs: true,
      adminControls: true,
      spendingLimits: true,
      sharedAIs: true,
      multipleUsers: true,
    },
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    priceUSD: 0, // Custom pricing
    aiCount: -1, // Unlimited
    memoryDepth: "unlimited",
    executionLimits: {
      messagesPerDay: -1,
      apiCallsPerDay: -1,
      fileUploadsPerDay: -1,
      mediaGenerationsPerMonth: -1,
    },
    agentAutonomy: 3,
    features: {
      chat: true,
      agentExecution: true,
      externalTools: true,
      persistentMemory: true,
      projects: true,
      knowledge: true,
      approvalPrompts: true,
      multiAgentWorkflows: true,
      auditLogs: true,
      adminControls: true,
      spendingLimits: true,
      sharedAIs: true,
      multipleUsers: true,
    },
  },
};

/**
 * Get subscription plan by tier
 */
export function getSubscriptionPlan(tier: SubscriptionTier): SubscriptionPlan {
  return SUBSCRIPTION_PLANS[tier];
}

/**
 * Check if tier has feature access
 */
export function hasFeatureAccess(
  tier: SubscriptionTier,
  feature: keyof SubscriptionPlan["features"]
): boolean {
  const plan = getSubscriptionPlan(tier);
  return plan.features[feature] === true;
}

/**
 * Check if tier supports agent autonomy level
 */
export function supportsAgentAutonomy(
  tier: SubscriptionTier,
  requiredLevel: 0 | 1 | 2 | 3
): boolean {
  const plan = getSubscriptionPlan(tier);
  return plan.agentAutonomy >= requiredLevel;
}

/**
 * Get minimum tier required for feature
 */
export function getMinimumTierForFeature(
  feature: keyof SubscriptionPlan["features"]
): SubscriptionTier {
  const tiers: SubscriptionTier[] = ["free", "personal", "pro", "business", "enterprise"];
  
  for (const tier of tiers) {
    if (hasFeatureAccess(tier, feature)) {
      return tier;
    }
  }
  
  return "enterprise"; // Fallback
}

/**
 * Check if execution limit is within plan
 */
export function checkExecutionLimit(
  tier: SubscriptionTier,
  limitType: keyof SubscriptionPlan["executionLimits"],
  currentUsage: number
): { withinLimit: boolean; limit: number; remaining?: number } {
  const plan = getSubscriptionPlan(tier);
  const limit = plan.executionLimits[limitType];
  
  if (limit === -1) {
    return { withinLimit: true, limit: -1 }; // Unlimited
  }
  
  const withinLimit = currentUsage < limit;
  const remaining = withinLimit ? limit - currentUsage : 0;
  
  return { withinLimit, limit, remaining };
}

